import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { User as FirebaseUser } from "firebase/auth";
import {
  GlobalCustomer,
  LoyaltyTier,
  LocationSettingsLoyalty,
} from "@sally-pos/pos-core";
import { auth } from "../firebase";
import { getProfile } from "../api";

// ─── Types ────────────────────────────────────────────────────────────────────

export type Customer = GlobalCustomer & { id: string };

/** LoyaltyTier enriched with discount info fetched server-side */
export type EnrichedLoyaltyTier = LoyaltyTier & {
  discountPercent?: number;
  discountName?: string;
};

export interface LoyaltyInfo {
  balance: number;
  spend: number;
  tier: EnrichedLoyaltyTier | null;
  nextTier: (EnrichedLoyaltyTier & { spendNeeded: number }) | null;
  expiresAt: string | null;
  /**
   * Set when this location's business shares loyalty: balance, tier and punch
   * cards are the business-wide ones, identical at each of its locations.
   */
  sharedBusinessId?: string | null;
  settings:
    | (Omit<LocationSettingsLoyalty, "tiers"> & {
        tiers: EnrichedLoyaltyTier[];
      })
    | null;
}

export interface CustomerPunchCardProgress {
  campaignId: string;
  name: string;
  punches: number;
  requiredQuantity: number;
  availableRewards: number;
  progress: number;
  reward: {
    type: "freeItem" | "fixedDiscount";
    maxValue?: number | null;
    amount?: number;
  };
  discountId?: string;
}

export interface LocationDetail {
  id: string;
  name: string;
  city?: string | null;
  currency?: string | null;
  logoURL?: string | null;
  orderingEnabled: boolean;
  loyalty: LoyaltyInfo | null;
  punchCards?: CustomerPunchCardProgress[];
  lastVisit?: string | null;
  devMode?: boolean;
}

/** A business whose locations share one loyalty balance. */
export interface LoyaltyBusiness {
  businessId: string;
  name: string;
  locationCount: number;
}

type AuthState = "loading" | "unauthenticated" | "authenticated";

export interface ICustomerContext {
  authState: AuthState;
  customer: Customer | null;
  locations: LocationDetail[];
  loyaltyBusinesses: LoyaltyBusiness[];
  dataLoading: boolean;
  error: string | null;
  noProfile: boolean;
  signOut: () => void;
  refreshProfile: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

export const CustomerContext = createContext<ICustomerContext>({
  authState: "loading",
  customer: null,
  locations: [],
  loyaltyBusinesses: [],
  dataLoading: false,
  error: null,
  noProfile: false,
  signOut: () => {},
  refreshProfile: () => {},
});

export const useCustomer = () => useContext(CustomerContext);

// ─── Wrapper ──────────────────────────────────────────────────────────────────

const CustomerContextWrapper: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [firebaseUser, setFirebaseUser] = useState<
    FirebaseUser | null | undefined
  >(undefined);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [locations, setLocations] = useState<LocationDetail[]>([]);
  const [loyaltyBusinesses, setLoyaltyBusinesses] = useState<LoyaltyBusiness[]>(
    [],
  );
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noProfile, setNoProfile] = useState(false);

  const profileRequest = useRef<ReturnType<typeof getProfile> | null>(null);

  const loadProfile = useCallback(async () => {
    setDataLoading(true);
    setError(null);
    setNoProfile(false);
    try {
      // Callers that overlap (the sign-in listener and the home screen, both
      // on app start) share one request instead of asking twice.
      profileRequest.current ??= getProfile().finally(() => {
        profileRequest.current = null;
      });
      const profileData = await profileRequest.current;

      // A signed-in guest who hasn't joined loyalty yet — normal, not a failure.
      if (!profileData.customer) {
        setNoProfile(true);
        setLocations([]);
        setLoyaltyBusinesses([]);
        return;
      }

      setCustomer(profileData.customer);
      setLocations((profileData.locations ?? []) as LocationDetail[]);
      setLoyaltyBusinesses(profileData.loyaltyBusinesses ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load your profile. Please try again.");
    } finally {
      setDataLoading(false);
    }
  }, []);

  const signOut = useCallback(() => auth.signOut(), []);
  const refreshProfile = useCallback(() => {
    loadProfile();
  }, [loadProfile]);

  const authState: AuthState =
    firebaseUser === undefined
      ? "loading"
      : firebaseUser
        ? "authenticated"
        : "unauthenticated";

  useEffect(() => {
    return auth.onAuthStateChanged((user) => {
      if (user) {
        setFirebaseUser(user);
        loadProfile();
      } else {
        setFirebaseUser(null);
        setCustomer(null);
        setLocations([]);
        setLoyaltyBusinesses([]);
        setError(null);
        setNoProfile(false);
      }
    });
  }, [loadProfile]);

  const contextValue = useMemo(
    () => ({
      authState,
      customer,
      locations,
      loyaltyBusinesses,
      dataLoading,
      error,
      noProfile,
      signOut,
      refreshProfile,
    }),
    [
      authState,
      customer,
      locations,
      loyaltyBusinesses,
      dataLoading,
      error,
      noProfile,
      signOut,
      refreshProfile,
    ],
  );

  return (
    <CustomerContext.Provider value={contextValue}>
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerContextWrapper;
